import { useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";
import Lucide from "src/base-components/Lucide";
import { normalizeHex } from "src/helpers/theme";

type ColorFormat = "hex" | "rgb" | "hsl";

interface ColorPickerPanelProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
}

const hexToRgb = (hex: string) => {
  const normalized = normalizeHex(hex).replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((channel) =>
      Math.max(0, Math.min(255, channel)).toString(16).padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`;

const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToRgb = (h: number, s: number, l: number) => {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
};

const paletteSwatches = [
  "#095CFD",
  "#0F9B8F",
  "#1A3A52",
  "#4C5FD5",
  "#7C3AED",
  "#E11D48",
  "#EA580C",
  "#FFC107",
  "#0F3460",
  "#2D9D78",
  "#00ACC1",
  "#FF6B5A",
];

const ColorPickerPanel = ({
  label,
  value,
  onChange,
  compact = false,
}: ColorPickerPanelProps) => {
  const [format, setFormat] = useState<ColorFormat>("hex");
  const safeValue = normalizeHex(value);
  const rgb = useMemo(() => hexToRgb(safeValue), [safeValue]);
  const hsl = useMemo(() => rgbToHsl(rgb.r, rgb.g, rgb.b), [rgb]);

  const handleEyedropper = async () => {
    if (!("EyeDropper" in window)) {
      return;
    }

    try {
      // @ts-expect-error EyeDropper is not in all TS DOM libs
      const dropper = new window.EyeDropper();
      const result = await dropper.open();
      onChange(normalizeHex(result.sRGBHex));
    } catch {
      // user cancelled
    }
  };

  return (
    <div
      className={`rounded-xl border border-white/10 bg-[#12141c] ${
        compact ? "space-y-2.5 p-3" : "space-y-3 p-4"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-white">{label}</p>
        <div className="flex items-center gap-2">
          {"EyeDropper" in window ? (
            <button
              type="button"
              onClick={handleEyedropper}
              className="rounded-lg border border-white/10 p-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white"
              aria-label="Pick color from screen"
            >
              <Lucide icon="Pipette" className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <span
            className="h-7 w-7 rounded-md border border-white/10"
            style={{ backgroundColor: safeValue }}
          />
        </div>
      </div>

      <div className={compact ? "theme-color-picker theme-color-picker--compact" : "theme-color-picker"}>
        <HexColorPicker
          color={safeValue}
          onChange={(next) => onChange(normalizeHex(next))}
        />
      </div>

      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          {(["hex", "rgb", "hsl"] as ColorFormat[]).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setFormat(item)}
              className={`rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition ${
                format === item
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {format === "hex" ? (
          <input
            value={safeValue}
            onChange={(event) => onChange(normalizeHex(event.target.value))}
            className="min-w-0 flex-1 rounded-lg border border-white/10 bg-[#0d1017] px-2.5 py-1.5 text-xs text-white outline-none focus:border-sky-400/60"
          />
        ) : null}
      </div>

      {format === "rgb" ? (
        <div className="grid grid-cols-3 gap-2">
          {(["r", "g", "b"] as const).map((channel) => (
            <input
              key={channel}
              type="number"
              min={0}
              max={255}
              value={rgb[channel]}
              onChange={(event) => {
                const next = { ...rgb, [channel]: Number(event.target.value) };
                onChange(rgbToHex(next.r, next.g, next.b));
              }}
              className="rounded-lg border border-white/10 bg-[#0d1017] px-2 py-1.5 text-xs text-white outline-none focus:border-sky-400/60"
            />
          ))}
        </div>
      ) : null}

      {format === "hsl" ? (
        <div className="grid grid-cols-3 gap-2">
          {(["h", "s", "l"] as const).map((channel) => (
            <input
              key={channel}
              type="number"
              min={0}
              max={channel === "h" ? 360 : 100}
              value={hsl[channel]}
              onChange={(event) => {
                const next = { ...hsl, [channel]: Number(event.target.value) };
                const converted = hslToRgb(next.h, next.s, next.l);
                onChange(rgbToHex(converted.r, converted.g, converted.b));
              }}
              className="rounded-lg border border-white/10 bg-[#0d1017] px-2 py-1.5 text-xs text-white outline-none focus:border-sky-400/60"
            />
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {paletteSwatches.map((swatch) => (
          <button
            key={`${label}-${swatch}`}
            type="button"
            aria-label={`Select ${swatch}`}
            onClick={() => onChange(swatch)}
            className={`h-6 w-6 rounded-full border transition hover:scale-110 ${
              safeValue === swatch
                ? "border-white ring-2 ring-sky-400/70"
                : "border-white/20"
            }`}
            style={{ backgroundColor: swatch }}
          />
        ))}
      </div>
    </div>
  );
};

export default ColorPickerPanel;
